import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './employment-details.component.scss';

interface EmploymentDetailsProps {
    startDate: string;
    endDate: string;
    isCurrentlyWorking: boolean;
    onStartDateChange: (date: string) => void;
    onEndDateChange: (date: string) => void;
    onCurrentlyWorkingChange: (checked: boolean) => void;
    errors?: { startDate?: string; endDate?: string };
}

export const EmploymentDetails: React.FC<EmploymentDetailsProps> = ({
    startDate,
    endDate,
    isCurrentlyWorking,
    onStartDateChange,
    onEndDateChange,
    onCurrentlyWorkingChange,
    errors
}) => {
    const { t } = useTranslation();

    return (
        <div className="employment-details">
            <div className="employment-details__card">
                <div className="employment-details__header">
                    <h3 className="employment-details__title">{t('Employment Details')}</h3>
                </div>
                
                <div className="employment-details__body">
                <div className="employment-details__row">
                    <div className="employment-details__field-group">
                        <label className="employment-details__label">
                            {t('Employment period')} :
                        </label>
                    </div>
                    
                    <div className="employment-details__date-fields">
                        <div className="employment-details__date-input">
                            <label className="employment-details__date-label">
                                {t('Start date')}
                            </label>
                            <div className="employment-details__input-wrapper">
                                <input
                                    type="date"
                                    className={`employment-details__input${errors?.startDate ? ' employment-details__input--error' : ''}`}
                                    placeholder="dd/mm/yyyy"
                                    value={startDate}
                                    max={new Date().toISOString().split('T')[0]}
                                    onChange={(e) => onStartDateChange(e.target.value)}
                                />
                                <span className="employment-details__calendar-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="19" viewBox="0 0 17 19" fill="none">
                                        <path d="M1 7.66667H16M5.16667 4.33333V1M11.8333 4.33333V1M6.33333 17.6667H10.6667C12.5335 17.6667 13.4669 17.6667 14.18 17.3034C14.8072 16.9838 15.3171 16.4738 15.6367 15.8466C16 15.1336 16 14.2002 16 12.3333V8C16 6.13316 16 5.19974 15.6367 4.4867C15.3171 3.85949 14.8072 3.34956 14.18 3.02998C13.4669 2.66667 12.5335 2.66667 10.6667 2.66667H6.33333C4.46649 2.66667 3.53307 2.66667 2.82003 3.02998C2.19282 3.34956 1.68289 3.85949 1.36331 4.4867C1 5.19974 1 6.13316 1 8V12.3333C1 14.2002 1 15.1336 1.36331 15.8466C1.68289 16.4738 2.19282 16.9838 2.82003 17.3034C3.53307 17.6667 4.46649 17.6667 6.33333 17.6667Z" stroke="#68727D" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                </span>
                            </div>
                            {errors?.startDate && <span className="employment-details__error-msg">{t(errors.startDate)}</span>}
                        </div>
                        
                        <div className="employment-details__date-input">
                            <label className="employment-details__date-label">
                                {t('End date')}
                            </label>
                            <div className="employment-details__input-wrapper">
                                <input
                                    type="date"
                                    className={`employment-details__input${errors?.endDate ? ' employment-details__input--error' : ''}`}
                                    placeholder="dd/mm/yyyy"
                                    value={endDate}
                                    max={new Date().toISOString().split('T')[0]}
                                    onChange={(e) => onEndDateChange(e.target.value)}
                                    disabled={isCurrentlyWorking}
                                />
                                <span className="employment-details__calendar-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="19" viewBox="0 0 17 19" fill="none">
                                        <path d="M1 7.66667H16M5.16667 4.33333V1M11.8333 4.33333V1M6.33333 17.6667H10.6667C12.5335 17.6667 13.4669 17.6667 14.18 17.3034C14.8072 16.9838 15.3171 16.4738 15.6367 15.8466C16 15.1336 16 14.2002 16 12.3333V8C16 6.13316 16 5.19974 15.6367 4.4867C15.3171 3.85949 14.8072 3.34956 14.18 3.02998C13.4669 2.66667 12.5335 2.66667 10.6667 2.66667H6.33333C4.46649 2.66667 3.53307 2.66667 2.82003 3.02998C2.19282 3.34956 1.68289 3.85949 1.36331 4.4867C1 5.19974 1 6.13316 1 8V12.3333C1 14.2002 1 15.1336 1.36331 15.8466C1.68289 16.4738 2.19282 16.9838 2.82003 17.3034C3.53307 17.6667 4.46649 17.6667 6.33333 17.6667Z" stroke="#68727D" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                </span>
                            </div>
                            {errors?.endDate && <span className="employment-details__error-msg">{t(errors.endDate)}</span>}
                        </div>
                        
                        <div className="employment-details__checkbox">
                            <label className="employment-details__checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={isCurrentlyWorking}
                                    onChange={(e) => {
                                        onCurrentlyWorkingChange(e.target.checked);
                                        if (e.target.checked) {
                                            onEndDateChange('');
                                        }
                                    }}
                                    className="employment-details__checkbox-input"
                                />
                                <span>{t('Currently working')}</span>
                            </label>
                        </div>
                    </div>
                </div>
                </div>
            </div>
        </div>
    );
};
